import {
  DEFAULT_FAVORED_REASONS,
  DEFAULT_NOT_FAVORED_REASONS,
  MANUAL_ENTRY_SUFFIX,
  ReasonTaxClass,
} from "@/constants/ReasonTaxClasses";

/** trim, NFC, collapse whitespace, case-insensitive. */
export function normalizeReason(reason: string): string {
  return reason.normalize("NFC").replace(/\s+/g, " ").trim().toLocaleLowerCase("de-DE");
}

const MANUAL_SUFFIX_PATTERN = new RegExp(
  `\\s*${MANUAL_ENTRY_SUFFIX.replace(/[()]/g, "\\$&").replace(/\s+/g, "\\s*")}\\s*$`,
  "i",
);

/** Splits an annotated reason into the cleaned reason and whether it was a manual entry. */
export function splitManualSuffix(rawReason: string | undefined): {
  reason: string | undefined;
  isManual: boolean;
} {
  if (!rawReason) return { reason: undefined, isManual: false };
  const trimmed = rawReason.normalize("NFC").trim();
  if (!trimmed || /^[\s\-–—]+$/.test(trimmed)) return { reason: undefined, isManual: false };
  if (!MANUAL_SUFFIX_PATTERN.test(trimmed)) return { reason: trimmed, isManual: false };
  const reason = trimmed.replace(MANUAL_SUFFIX_PATTERN, "").trim();
  return { reason: reason || undefined, isManual: true };
}

export type ReasonTaxClassMap = Map<string, ReasonTaxClass>;

/** Default assignment plus user overrides, keyed by normalized reason. */
export function buildReasonTaxClassMap(
  overrides: Record<string, ReasonTaxClass> = {},
): ReasonTaxClassMap {
  const map: ReasonTaxClassMap = new Map();
  for (const reason of DEFAULT_FAVORED_REASONS) map.set(normalizeReason(reason), "favored");
  for (const reason of DEFAULT_NOT_FAVORED_REASONS) map.set(normalizeReason(reason), "notFavored");
  for (const [reason, taxClass] of Object.entries(overrides)) {
    map.set(normalizeReason(reason), taxClass);
  }
  return map;
}

export function classifyReason(
  reason: string | undefined,
  taxClasses: ReasonTaxClassMap,
): ReasonTaxClass {
  if (!reason) return "none";
  return taxClasses.get(normalizeReason(reason)) ?? "none";
}

/** The default class of a reason, ignoring overrides. */
export function defaultReasonTaxClass(reason: string): ReasonTaxClass {
  return classifyReason(reason, buildReasonTaxClassMap());
}
